import { useMsal, useIsAuthenticated } from "@azure/msal-react"
import { InteractionStatus } from "@azure/msal-browser"
import { Fragment, useEffect } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { useAppSelector } from "@/store"
import { ensureStaffSession, staffSessionRuntime } from "@/store/staffLifecycle"
import { staffAccountKey } from "./staffAccount"
import { apiScopes } from "./msalConfig"
import { Button } from "@/components/ui/button"
import { IconLoader2 } from "@tabler/icons-react"

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated()
  const { inProgress, instance } = useMsal()
  const location = useLocation()
  const session = useAppSelector(state => state.staffSession)
  const account = staffAccountKey()

  useEffect(() => {
    if (isAuthenticated) void ensureStaffSession()
    else void staffSessionRuntime.dispose().catch(() => {})
    // No cleanup stop: this is the tab's session, not a component-owned socket.
  }, [isAuthenticated, account])

  if (inProgress !== InteractionStatus.Startup && inProgress !== InteractionStatus.HandleRedirect && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  if (session.phase === "unavailable" && !session.scopeKey) {
    return <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
      <p role="alert">Your staff access could not be verified. Protected data has been cleared.</p>
      <div className="flex gap-2">
        <Button onClick={() => { void staffSessionRuntime.refresh() }}>Retry access check</Button>
        <Button variant="outline" onClick={() => { void instance.acquireTokenRedirect(apiScopes) }}>Sign in again</Button>
      </div>
    </div>
  }
  // Compare against the live active account during render, before effects run.
  // A stored scope alone cannot authorize rendering a previous account's data.
  if (!session.scopeKey || !staffSessionRuntime.isCurrent(account) ||
      inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return <div className="flex h-screen w-full items-center justify-center">
      <IconLoader2 className="size-8 animate-spin text-muted-foreground" aria-label="Checking staff access" />
    </div>
  }
  // Also purges legacy component-local drafts when effective access changes.
  // This is not navigation cancellation and does not undo a pending server write.
  return <>
    {session.phase === "unavailable" && <div role="alert" className="border-b bg-muted p-3 text-center">
      Access refresh is unavailable. Your draft is retained read-only.
      <Button variant="outline" size="sm" className="ml-3" onClick={() => { void staffSessionRuntime.refresh() }}>Retry access check</Button>
    </div>}
    <fieldset disabled={session.phase === "unavailable"} className="contents" key={`${session.scopeKey}/${session.epoch}`}>
      <Fragment>{children}</Fragment>
    </fieldset>
  </>
}
